import { BigNumber } from 'ethers'

import { useMemo } from 'react'
import { useSWR } from 'modules/network/hooks/useSwr'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'

import { ContractAddressBook } from 'modules/contracts/contracts'
import { PoolAsset } from 'modules/pools/constants/poolAssets'
import { PoolRisk } from 'modules/pools/constants/PoolRisk'
import { DepositLimit } from 'modules/pools/types/DepositLimit'
import { strToWei, weiToNum } from 'modules/blockChain/utils/parseWei'
import { getPoolInvestorByAssetAndRisk } from 'modules/pools/utils/getPoolInvestorContract'
import { getLimitErrorDeposit } from 'modules/pools/utils/getLimitErrorDeposit'
import { getLimitsMock } from 'modules/pools/utils/getLimitsMock'
import * as formErrors from 'shared/constants/formErrors'

type Props = {
  amount: string
  depositedAsset: '' | PoolAsset
  risk: '' | PoolRisk
}

export const useDepositLimit = (props: Props) => {
  const { amount, depositedAsset, risk } = props
  const { chainId } = useWeb3()
  const { getAssetDecimals } = useAssetsData()

  const { data, isValidating } = useSWR<DepositLimit | undefined>(
    depositedAsset && risk
      ? `deposit-limit-${chainId}-${risk}=${depositedAsset}`
      : null,
    async () => {
      if (depositedAsset === '' || !risk) return

      const addressBook = ContractAddressBook.connectRpc({ chainId })
      const { contract } = getPoolInvestorByAssetAndRisk(depositedAsset, risk)
      const poolInvestor = contract.connectRpc({ chainId })

      const [[totalValue, dailyValue], poolDailyLimit, poolGlobalLimit] =
        await Promise.all([
          poolInvestor.limits(),
          addressBook.poolDailyLimit(poolInvestor.address),
          addressBook.poolGlobalLimit(poolInvestor.address),
        ])

      const mock = getLimitsMock()

      return {
        totalValue: mock?.totalValue || totalValue,
        dailyValue: mock?.dailyValue || dailyValue,
        poolDailyLimit: mock?.poolDailyLimit || poolDailyLimit,
        poolGlobalLimit: mock?.poolGlobalLimit || poolGlobalLimit,
      }
    },
    {
      refreshInterval: 10000,
    },
  )

  const limitError = useMemo(() => {
    if (!data || !amount.length) return null

    let amountWei: BigNumber

    try {
      amountWei = strToWei(amount, getAssetDecimals(depositedAsset))
    } catch (err) {
      return formErrors.valueParse
    }

    return getLimitErrorDeposit({
      ...data,
      amountWei,
    })
  }, [amount, data, getAssetDecimals, depositedAsset])

  const maxByLimit = useMemo(() => {
    if (!data) return

    const decimals = getAssetDecimals(depositedAsset)

    const max1 = data.poolDailyLimit.isZero()
      ? Infinity
      : weiToNum(data.poolDailyLimit.sub(data.dailyValue), decimals)

    const max2 = data.poolGlobalLimit.isZero()
      ? Infinity
      : weiToNum(data.poolGlobalLimit.sub(data.totalValue), decimals)

    return Math.max(Math.min(max1, max2), 0)
  }, [data, getAssetDecimals, depositedAsset])

  return {
    limitError,
    maxByLimit,
    exceedsLimit: Boolean(limitError),
    isLoading: isValidating,
  }
}
